import { signalPresentation, type SignalColor, type SignalItem } from '@indipia/shared'
import type { OrderVendorRow } from '../api/orderApi'
import {
  STAGE_KIND_LABEL, STAGE_STEP_NAMES,
  type StageCell, type StageKind, type StageState, type StageStepState,
} from '../types/scheduleList.types'
import { ASSIGN_METHOD, getMaterialProgressSteps, ORDER_STATUS_LABEL } from './materialOrder.logic'
import { ASSIGN_SOURCE, getConfirmProgressSteps, type PartnerStatusLineTask } from './partnerStatus'
import { getWorkProgressSteps, isReportApproved, WORK_PROGRESS_TEXT, type WorkProgressQuery } from './taskProgress.logic'
import { isSignalColor, signalCtx } from './signalView'

// 리스트 보기 3칸 단계 판정(B-01~03)과 펼침 줄 단계 문구(B-04) — 구현계획 3절 · L1_C6_신규사양 §2·§6.
//
//  - 단계 위치(d·c)는 신호 reasonCode 로만 정한다. 색은 서버 colorCode 를 그대로 쓰고(색 덮어쓰기 = 서버가 이미 반영한
//    값: 작업요청 대기 착공 D-1 = 빨강 포함), 흰색·녹색·파랑으로 고정되는 칸만 이 파일이 고정한다. 날짜로 색을 다시 계산하지 않는다.
//  - 신호가 없으면 'no-signal', 이 칸의 코드가 아니거나 모르는 코드·색이면 'unknown' — 값을 만들어 채우지 않는다.
//  - 발주 해당 없음(ORDER_NA)은 'none'. 거래처 없음은 상태 글자 '거래처 미배정'·흰색 고정(표시만, 신호 상태·할일 아님).
//  - 기존 partnerStatus.ts·materialOrder.logic.ts·taskProgress.logic.ts 는 수정하지 않고 문구 함수만 재사용한다.

/** 한 칸의 위치 정의 */
interface StagePos {
  d: number
  c: number | null
  /** 고정 색. null 이면 서버 색 */
  fixed: SignalColor | null
}

const sv = (d: number, c: number): StagePos => ({ d, c, fixed: null })

/** 반장 4단계(신본 pst) — 반장 슬롯 코드 11종 전부 */
const FOREMAN_POS: Record<string, StagePos> = {
  FOREMAN_UNASSIGNED: { d: 0, c: 0, fixed: 'WHITE' },
  FOREMAN_UNASSIGNABLE: sv(0, 0),
  FOREMAN_UNASSIGNED_SOON: sv(0, 0),
  FOREMAN_ASSIGNED_AUTO: sv(1, 1),
  FOREMAN_ASSIGNED_MANUAL: sv(1, 1),
  FOREMAN_CONFIRMED: { d: 4, c: null, fixed: 'GREEN' },
  FOREMAN_WAITING: sv(2, 2),
  FOREMAN_NEXT_WAITING: sv(2, 2),
  FOREMAN_REWAITING: sv(2, 2),
  FOREMAN_DECLINED: sv(2, 2),
  FOREMAN_UNFIXED: sv(2, 2),
}

/**
 * 발주 6단계(신본 ost) — ORDER_NA·거래처 없음은 함수에서 먼저 처리.
 * 서버 코드 3종은 신본 사전에 없어 확정 방침으로 위치를 정함(계획서 6-1):
 *  ORDER_RESEND_NEEDED = ②발주정보 요청 · ORDER_TODAY_DELIVERY·ORDER_RECEIPT_ISSUE = 신본 '그 밖'(⑥ 입고).
 */
const ORDER_POS: Record<string, StagePos> = {
  ORDER_REASSIGN_NEEDED: sv(0, 0),
  ORDER_BEFORE_DATE: { d: 1, c: null, fixed: 'WHITE' },
  ORDER_SEND_DUE: sv(1, 1),
  ORDER_SEND_OVERDUE: sv(1, 1),
  ORDER_RESEND_NEEDED: sv(1, 1),
  ORDER_REPLY_WAIT: sv(2, 2),
  ORDER_REREPLY_WAIT: sv(2, 2),
  ORDER_CONFIRMED: { d: 3, c: null, fixed: 'GREEN' },
  ORDER_SHIP_INFO_WAIT: sv(4, 4),
  ORDER_RECEIVED: { d: 6, c: null, fixed: 'GREEN' },
  ORDER_RECEIPT_WAIT: sv(5, 5),
  ORDER_RECEIPT_DELAY: sv(5, 5),
  ORDER_TODAY_DELIVERY: sv(5, 5),
  ORDER_RECEIPT_ISSUE: sv(5, 5),
}

/** 작업 3단계(신본 wst). 이슈보고는 신본 사양대로 ③ 위치(계획서 6-2) */
const WORK_POS: Record<string, StagePos> = {
  WORK_NOT_REQUESTED: { d: 0, c: null, fixed: 'WHITE' },
  WORK_REQUEST_DUE: sv(0, 0),
  WORK_INSPECT_WAIT: sv(2, 2),
  WORK_REPORT_ISSUE: sv(2, 2),
  WORK_DONE: { d: 3, c: null, fixed: 'BLUE' },
  WORK_REPORT_WAIT: sv(1, 1),
  WORK_REPORT_PROGRESS: sv(1, 1),
  WORK_REPORT_DELAY: sv(1, 1),
}

const UNKNOWN = (item: SignalItem): StageCell => ({ status: 'unknown', reasonCode: item.reasonCode })

/** 서버 색을 쓰는 칸의 색 검증 — 모르는 값·해당 없음(NONE)은 표시하지 않는다 */
function serverColor(item: SignalItem): SignalColor | null {
  const c = item.colorCode
  return isSignalColor(c) && c !== 'NONE' ? c : null
}

/** 칸 판정 공통 — 슬롯 확인·위치표 조회·색 결정·상태 글자 */
function buildStage(
  kind: StageKind, item: SignalItem, pos: StagePos | undefined,
  over?: { label: string; color: SignalColor; d: number; c: number | null; vendorMissing: boolean },
): StageCell {
  if (item.signalTypeCode !== kind) return UNKNOWN(item)
  if (!pos) return UNKNOWN(item)
  const color = over?.color ?? pos.fixed ?? serverColor(item)
  if (!color) return UNKNOWN(item)
  const label = over?.label ?? signalPresentation(item.reasonCode)?.name
  if (!label) return UNKNOWN(item)
  const stage: StageState = {
    kind,
    d: over?.d ?? pos.d,
    c: over ? over.c : pos.c,
    color,
    label,
    stepCount: STAGE_STEP_NAMES[kind].length,
    reasonCode: item.reasonCode,
    vendorMissing: over?.vendorMissing ?? false,
  }
  return { status: 'stage', stage }
}

/** B-01 반장 배정 4단계. 신호 없음 = 'no-signal', 모르는 코드 = 'unknown', 자재시공(FOREMAN_NA) = 'none'(빗금 해당 없음) */
export function foremanStage(item: SignalItem | undefined): StageCell {
  if (!item) return { status: 'no-signal' }
  if (item.signalTypeCode === 'FOREMAN' && item.reasonCode === 'FOREMAN_NA') return { status: 'none', slot: 'FOREMAN' }
  return buildStage('FOREMAN', item, FOREMAN_POS[item.reasonCode])
}

/**
 * B-02 발주·입고 6단계. 판정 순서: ①ORDER_NA → 'none' ②거래처 없음 → '거래처 미배정'·d0·c0·흰색 고정 ③ 이후 코드표.
 * 거래처 없음이어도 모르는 코드는 'unknown'(숨기지 않음). hasVendor = 이 공종에 원장 거래처가 있음.
 */
export function orderStage(item: SignalItem | undefined, opts: { hasVendor: boolean }): StageCell {
  if (!item) return { status: 'no-signal' }
  if (item.signalTypeCode !== 'ORDER') return UNKNOWN(item)
  if (item.reasonCode === 'ORDER_NA') return { status: 'none' }
  const pos = ORDER_POS[item.reasonCode]
  if (!pos) return UNKNOWN(item)
  if (!opts.hasVendor) {
    return buildStage('ORDER', item, pos, { label: '거래처 미배정', color: 'WHITE', d: 0, c: 0, vendorMissing: true })
  }
  return buildStage('ORDER', item, pos)
}

/** B-03 작업요청·검수 3단계 */
export function workStage(item: SignalItem | undefined): StageCell {
  if (!item) return { status: 'no-signal' }
  return buildStage('WORK', item, WORK_POS[item.reasonCode])
}

/** 막대 한 칸의 상태 — 지금(c) 우선, 끝남(i<d), 그 밖은 예정 */
export function stepStateAt(i: number, d: number, c: number | null): StageStepState {
  if (c != null && i === c) return 'current'
  return i < d ? 'done' : 'pending'
}

/** 칸 → 단계 막대 상태 배열(길이 = stepCount). stage 가 아닌 칸은 빈 배열 */
export function stageStepStates(cell: StageCell): StageStepState[] {
  if (cell.status !== 'stage') return []
  const { d, c, stepCount } = cell.stage
  return Array.from({ length: stepCount }, (_, i) => stepStateAt(i, d, c))
}

// ── B-04 펼침 줄 ───────────────────────────────────────────────────────

/** 펼침 줄의 단계 한 칸 */
export interface ListLaneStep {
  /** 신본 단계 이름(STAGE_STEP_NAMES) */
  title: string
  /** 기존 진행 요약 문구. 값이 없는 단계는 '-'(채우지 않음) */
  text: string
  state: StageStepState
  detailKey?: string
}

/** 펼침 줄 1개. notice 가 있으면 타임라인 없이 이 문구만 보인다(steps 는 빈 배열) */
export interface ListLane {
  kind: StageKind
  /** 줄 이름 */
  title: string
  /** 줄 머리의 상대방 이름 */
  counterpart: string
  /** 상태 글자(지금 단계 라벨). stage 가 아니면 null */
  statusLabel: string | null
  /** 상태색. stage 가 아니면 null */
  color: SignalColor | null
  /** 지금 단계 사유 = signalPresentation(...).line. 지금 단계가 없으면 null */
  reason: string | null
  steps: ListLaneStep[]
  notice: string | null
}

/** 발주 해당 없음 줄 문구(신본 §6-1) */
export const LANE_ORDER_NA_TEXT = '발주하지 않는 공정 (시공파트너 자재 포함)'
/** 거래처 미배정 줄의 사유(신본 §6-2) */
export const LANE_VENDOR_MISSING_REASON = '자동매칭 · 직접 지정 전'
/** 신호 없음·모르는 상태 문구(계획서 6-16) */
/** 시공 해당 없음(자재시공 공종) 줄 문구 */
export const LANE_FOREMAN_NA_TEXT = '자재파트너가 시공하는 공정'
export const LANE_NO_SIGNAL_TEXT = '신호 없음'
export const LANE_UNKNOWN_TEXT = '표시할 수 없는 신호 상태'

const FOREMAN_NONE_NAME = '반장 미배정'
const FOREMAN_NA_NAME = '자재파트너'
const VENDOR_NONE_NAME = '거래처 미배정'

/** 'YYYY-MM-DD' → 로컬 달력일 Date */
function localDate(day: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(day)
  if (!m) return new Date(NaN)
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}

/** 공통 입력 */
interface LaneBase {
  cell: StageCell
  /** 칸을 정한 신호 원본(문구 조립 값) */
  item: SignalItem | undefined
  /** 'YYYY-MM-DD' (호출 쪽이 isoDay(new Date()) 로 넘김) */
  today: string
  startDate: string
  endDate: string
}

function emptyLane(kind: StageKind, counterpart: string, notice: string): ListLane {
  return { kind, title: STAGE_KIND_LABEL[kind], counterpart, statusLabel: null, color: null, reason: null, steps: [], notice }
}

/** 신호 없음·모르는 상태 줄 — stage 가 아니고 none 도 아닌 칸 */
function noticeOf(cell: StageCell, kind?: StageKind): string | null {
  switch (cell.status) {
    case 'no-signal': return LANE_NO_SIGNAL_TEXT
    case 'unknown': return LANE_UNKNOWN_TEXT
    case 'none': return kind === 'FOREMAN' || cell.slot === 'FOREMAN' ? LANE_FOREMAN_NA_TEXT : LANE_ORDER_NA_TEXT
    default: return null
  }
}

/** 단계 이름을 신본으로 바꾸고 상태를 d·c 로 덮어쓴다 */
function overlay(kind: StageKind, raw: { text: string; detailKey?: string }[], stage: StageState): ListLaneStep[] {
  const names = STAGE_STEP_NAMES[kind]
  return names.map((title, i) => {
    const r = raw[i]
    return {
      title,
      text: r?.text ?? '-',
      state: stepStateAt(i, stage.d, stage.c),
      ...(r?.detailKey && { detailKey: r.detailKey }),
    }
  })
}

/** 지금 단계 사유 — 지금 단계(c)가 있을 때만. 거래처 미배정은 신본 문구 */
function reasonOf(stage: StageState, item: SignalItem | undefined, ctx: Parameters<typeof signalCtx>[1] & { today: string }): string | null {
  if (stage.c == null) return null
  if (stage.vendorMissing) return LANE_VENDOR_MISSING_REASON
  if (!item) return null
  const { today, ...t } = ctx
  return signalPresentation(item.reasonCode, { ...signalCtx(item, t), today })?.line ?? null
}

/** 일정확인 방식 글자 */
function assignWay(source: string | null | undefined, auto: string, manual: string): string | null {
  if (source === auto) return '자동배정'
  if (source === manual) return '직접배정'
  return null
}

/**
 * 반장 배정 줄. 1단계 '시공파트너 배정'(반장 이름·자동/직접배정 또는 '반장 미배정') +
 * getConfirmProgressSteps 3단계 문구 재사용. 상태는 B-01 의 d·c 로 덮어씀.
 */
export function getForemanLaneSteps(
  input: LaneBase & { task: PartnerStatusLineTask; partnerName: string | null; rank?: number | null },
): ListLane {
  const { cell, item, task, partnerName } = input
  const counterpart = partnerName ?? (cell.status === 'none' ? FOREMAN_NA_NAME : FOREMAN_NONE_NAME)
  const notice = noticeOf(cell, 'FOREMAN')
  if (notice || cell.status !== 'stage') return emptyLane('FOREMAN', counterpart, notice ?? LANE_UNKNOWN_TEXT)
  const stage = cell.stage
  const confirm = getConfirmProgressSteps(task, {
    hasPartner: partnerName != null,
    startDate: localDate(input.startDate),
    endDate: localDate(input.endDate),
  })
  const way = assignWay(task.assignSource, ASSIGN_SOURCE.AUTO, ASSIGN_SOURCE.MANUAL)
  const first = partnerName ? [partnerName, way].filter(Boolean).join(' · ') : FOREMAN_NONE_NAME
  return {
    kind: 'FOREMAN',
    title: STAGE_KIND_LABEL.FOREMAN,
    counterpart,
    statusLabel: stage.label,
    color: stage.color,
    reason: reasonOf(stage, item, {
      today: input.today, startDate: input.startDate, endDate: input.endDate, partnerName, rank: input.rank ?? task.confirmRequest?.rank ?? null,
    }),
    steps: overlay('FOREMAN', [{ text: first }, ...confirm], stage),
    notice: null,
  }
}

/**
 * 발주·입고 줄. 1단계 '자재파트너 배정'(거래처 이름·배정 방식 또는 '거래처 미배정') +
 * getMaterialProgressSteps 5단계 문구 재사용(단계 이름은 신본 이름으로 교체). row = B-05 대표 원장 행(없으면 null).
 */
export function getOrderLaneSteps(
  input: LaneBase & { row: OrderVendorRow | null; vendorName: string | null; orderDate?: string | null },
): ListLane {
  const { cell, item, row } = input
  const vendorName = input.vendorName ?? row?.vendorName ?? null
  const counterpart = vendorName ?? VENDOR_NONE_NAME
  const notice = noticeOf(cell)
  if (notice || cell.status !== 'stage') return emptyLane('ORDER', counterpart, notice ?? LANE_UNKNOWN_TEXT)
  const stage = cell.stage
  const mat = getMaterialProgressSteps(row, { orderDate: input.orderDate ?? null })
  const way = assignWay(row?.assignMethodCode, ASSIGN_METHOD.AUTO, ASSIGN_METHOD.MANUAL)
  const first = vendorName ? [vendorName, way].filter(Boolean).join(' · ') : VENDOR_NONE_NAME
  return {
    kind: 'ORDER',
    title: STAGE_KIND_LABEL.ORDER,
    counterpart,
    statusLabel: stage.label,
    color: stage.color,
    reason: reasonOf(stage, item, {
      today: input.today, startDate: input.startDate, endDate: input.endDate, vendorName,
    }),
    steps: overlay('ORDER', [{ text: first }, ...mat], stage),
    notice: null,
  }
}

/**
 * 한 공종에 자재파트너가 여러 곳일 때 대표가 아닌 거래처의 발주·입고 줄(SIF-2).
 * 서버 신호(ORDER)는 공종당 대표 거래처 1곳 기준이라, 나머지 거래처는 그 원장 행만으로 그린다 —
 * 단계 상태는 getMaterialProgressSteps(행 상태·발송·회신·배송·입고 기록), 상태 글자는 발주 상태 이름. 신호색이 없어 점·지금 단계는 중립색.
 */
export function getVendorOrderLane(row: OrderVendorRow): ListLane {
  const mat = getMaterialProgressSteps(row, { orderDate: row.orderDate })
  const way = assignWay(row.assignMethodCode, ASSIGN_METHOD.AUTO, ASSIGN_METHOD.MANUAL)
  const first = { text: [row.vendorName, way].filter(Boolean).join(' · '), state: 'done' as StageStepState }
  const raw = [first, ...mat]
  return {
    kind: 'ORDER',
    title: STAGE_KIND_LABEL.ORDER,
    counterpart: row.vendorName ?? VENDOR_NONE_NAME,
    statusLabel: (ORDER_STATUS_LABEL as Record<string, string | undefined>)[row.orderStatusCode] ?? null,
    color: null,
    reason: null,
    steps: STAGE_STEP_NAMES.ORDER.map((title, i) => {
      const r = raw[i] as { text: string; state: StageStepState; detailKey?: string } | undefined
      return { title, text: r?.text ?? '-', state: r?.state ?? 'pending', ...(r?.detailKey && { detailKey: r.detailKey }) }
    }),
    notice: null,
  }
}

/**
 * 작업요청·검수 줄. getWorkProgressSteps 2단계(작업요청·작업보고) + '검수완료' 3단계를 isReportApproved 로 분리.
 * progress = useTaskProgressSummary 결과(펼칠 때만 조회), workdays = 공종 작업일 수(모르면 null).
 */
export function getWorkLaneSteps(
  input: LaneBase & { progress: WorkProgressQuery; workdays: number | null; partnerName: string | null; /** 시공 칸이 해당 없음(자재시공)이면 true — 상대방을 '반장 미배정'으로 쓰지 않는다 */ foremanNa?: boolean },
): ListLane {
  const { cell, item, progress } = input
  const counterpart = input.partnerName ?? (input.foremanNa ? FOREMAN_NA_NAME : FOREMAN_NONE_NAME)
  const notice = noticeOf(cell, 'WORK')
  if (notice || cell.status !== 'stage') return emptyLane('WORK', counterpart, notice ?? LANE_UNKNOWN_TEXT)
  const stage = cell.stage
  const two = getWorkProgressSteps(progress, input.workdays)
  let third: string
  if (progress.status === 'loading') third = WORK_PROGRESS_TEXT.loading
  else if (progress.status === 'error') third = WORK_PROGRESS_TEXT.loadError
  else third = progress.summary.reportDayCount > 0 && isReportApproved(progress.summary.reportDays) ? WORK_PROGRESS_TEXT.approved : WORK_PROGRESS_TEXT.none
  return {
    kind: 'WORK',
    title: STAGE_KIND_LABEL.WORK,
    counterpart,
    statusLabel: stage.label,
    color: stage.color,
    reason: reasonOf(stage, item, {
      today: input.today, startDate: input.startDate, endDate: input.endDate, partnerName: input.partnerName,
    }),
    steps: overlay('WORK', [...two, { text: third }], stage),
    notice: null,
  }
}

